import { createAsyncThunk, createSlice, PayloadAction, } from "@reduxjs/toolkit";
import PaginatedResponse from "common/contracts/paginated-response";
import Document from "documents/entities/document/document";
import DocumentAPIService, {
  DocumentQuery
} from "documents/entities/document/api/document-api-service";
import Session from "users/session/session";
import Guid from "common/values/guid/guid";
import UnavailableDocument from "documents/values/unavailable-document";
import { RootState } from "app/realtime-store/redux-store";
import { useSelector } from "react-redux";
import _ from "lodash";
import { AxiosError } from "axios";

type DocumentStoreState = {
  byQuery: {
    entries: Record<string, PaginatedResponse<Document>>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  };
  byId: {
    entries: Record<string, Document | UnavailableDocument>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  };
  interfaceQueries: {
    entries: Record<string, DocumentQuery>;
  };
};

const initialState: DocumentStoreState = {
  byQuery: {
    entries: {},
    loading: {},
    error: {},
  },
  byId: {
    entries: {},
    loading: {},
    error: {},
  },
  interfaceQueries: {
    entries: {}
  },
};

export const populateDocumentsByQuery = createAsyncThunk<
  PaginatedResponse<Document>,
  { session: Session; query: DocumentQuery }
>(
  "documents/getDocumentsByQuery",
  async (
    { session, query }: { session: Session; query: DocumentQuery },
    thunkAPI
  ) => {
    const apiService = new DocumentAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await apiService.getDocumentsInfo(
        query,
        abortController
      );
    } catch (error) {
      const apiError = error as AxiosError;
      return thunkAPI.rejectWithValue(
        apiError?.response?.data ?? error?.toString() ?? "An error occurred."
      );
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  }
);

export const populateDocuments = createAsyncThunk<
  (Document | UnavailableDocument)[],
  { session: Session; documentIds: Guid[] }
>(
  "documents/getDocumentsByIds",
  async ({ session, documentIds }: { session: Session; documentIds: Guid[] }, thunkAPI) => {
    if (!documentIds.length) return [];

    const store: { documents: DocumentStoreState } = thunkAPI.getState() as { documents: DocumentStoreState };
    const missingDocumentsIds = _.uniqWith(
      documentIds.filter((documentId) => !store.documents.byId.entries[documentId.value]),
      (a, b) => a.isEqualTo(b)
    );
    if (!missingDocumentsIds.length) return [];

    const apiService = new DocumentAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await apiService.getDocumentsInfoByIds(documentIds, abortController);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  }
);
export const documentsSlice = createSlice({
  name: "documents",
  initialState,
  reducers: {
    addDocument: (state, action: PayloadAction<Document | UnavailableDocument>) => {
      state.byId.entries[action.payload.id.value] = action.payload;
      state.byQuery = {
        entries: {},
        loading: {},
        error: {},
      };
    },
    removeDocument: (state, action: PayloadAction<Document | UnavailableDocument | Guid>) => {
      let documentId: Guid | undefined;
      if (action.payload instanceof Guid) {
        documentId = action.payload;
      } else {
        documentId = action.payload.id;
      }
      if (!documentId) {
        throw new Error("Document id not found");
      }
      delete state.byId.entries[documentId.value];
      state.byQuery = {
        entries: {},
        loading: {},
        error: {},
      };
    },
    updateDocumentsInterfaceQuery: (
      state,
      action: PayloadAction<{
        interfaceName: string;
        query: DocumentQuery;
      }>
    ) => {
      state.interfaceQueries.entries[action.payload.interfaceName] =
        action.payload.query;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      populateDocuments.pending,
      (state, action) => {
        const documentIds = action.meta.arg.documentIds
          ;
        if (!documentIds.length) {
          return;
        }
        for (const id of documentIds) {
          state.byId.loading[id.value] = true;
          state.byId.error[id.value] = null;
        }
      }
    );
    builder.addCase(
      populateDocuments.fulfilled,
      (state, action) => {
        const documentIds = action.meta.arg.documentIds;
        if (!documentIds.length) {
          return;
        }
        for (const doc of action.payload) {
          state.byId.entries[doc.id.value] = doc;
        }

        for (const id of documentIds) {
          state.byId.loading[id.value] = false;
          if (!state.byId.entries[id.value]) {
            state.byId.error[id.value] = "Document not found";
          }
        }
      });

    builder.addCase(
      populateDocuments.rejected,
      (state, action) => {
        const documentIds = action.meta.arg.documentIds;
        if (!documentIds.length) {
          return;
        }
        for (const id of documentIds) {
          state.byId.loading[id.value] = false;
          state.byId.error[id.value] = "Error fetching document info.";
        }
      }
    );

    builder.addCase(
      populateDocumentsByQuery.pending,
      (state, action) => {
        const query = action.meta.arg.query.asSearchParams().toString();
        state.byQuery.loading[query] = true;
        state.byQuery.error[query] = null;
      }
    );
    builder.addCase(
      populateDocumentsByQuery.fulfilled,
      (state, action) => {
        const query = action.meta.arg.query.asSearchParams().toString();
        state.byQuery.entries[query] = action.payload;
        const documents = action.payload.data;
        for (const doc of documents) {
          state.byId.entries[doc.id.value] = doc;
          state.byId.loading[doc.id.value] = false;
          state.byId.error[doc.id.value] = null;
        }
        state.byQuery.loading[query] = false;
      }
    );
    builder.addCase(
      populateDocumentsByQuery.rejected,
      (state, action) => {
        const query = action.meta.arg.query.asSearchParams().toString();
        state.byQuery.loading[query] = false;
        state.byQuery.error[query] = action.payload as string;
      }
    );
  },
});

export const { addDocument, removeDocument, updateDocumentsInterfaceQuery } = documentsSlice.actions;

export const getDocumentsByQuery = (query: DocumentQuery) => {
  return useSelector((state: RootState) => {
    if (!query) {
      return undefined;
    }
    return state.documents.byQuery.entries[
      query.asSearchParams().toString()
    ];
  });
};
export const getIsLoadingDocumentsByQuery = (query: DocumentQuery) =>
  useSelector((state: RootState) => {
    if (!query) return false;
    return state.documents.byQuery.loading[
      query.asSearchParams().toString()
    ] ?? false;
  });
export const getErrorLoadingDocumentsByQuery = (
  query: DocumentQuery
) =>
  useSelector(
    (state: RootState) => {
      if (!query) return false;
      return state.documents.byQuery.error[query.asSearchParams().toString()] ?? false;
    }
  );

export const getDocumentById = (id: Guid | undefined) => {
  return useSelector((state: RootState) => {
    if (!id) {
      return undefined;
    }
    return state.documents.byId.entries[
      id.toString()
    ];
  });
};
export const getDocumentIsLoading = (id: Guid | undefined) => {
  return useSelector((state: RootState) => {
    if (!id) return false;
    return state.documents.byId.loading[
      id.toString()
    ];
  });
};

export const getDocumentsByIds = (documentIds: Guid[]) => {
  const state = useSelector((state: RootState) => state.documents.byId.entries);
  return documentIds.map((documentId) => state[documentId.value]).filter((document) => document !== undefined);
}
export const getLoadingDocumentIds = () => {
  const state = useSelector((state: RootState) => state.documents.byId.loading);
  return Object.keys(state).filter((documentId) => state[documentId]).map((documentId) => Guid.fromJSON(documentId));
}

export const getDocumentError = (id: Guid | undefined) => {
  return useSelector((state: RootState) => {
    if (!id) {
      return undefined;
    }
    return state.documents.byId.error[
      id.toString()
    ];
  });
};
export type DocumentError = {
  id: Guid;
  error: string;
};
export const getAllDocumentErrors = (): DocumentError[] => {
  const state = useSelector((state: RootState) => state.documents.byId.error);
  return Object.keys(state)
    .filter((documentId) => documentId && state[documentId])
    .map((documentId) => {
      const id = Guid.fromJSON(documentId);
      const error = state[documentId];
      if (!id || !error) return null;
      return { id, error } as DocumentError;
    })
    .filter((item): item is DocumentError => item !== null);
};
export const getAreErrorsLoadingDocumentsByIds = (documentIds: Guid[]) => {
  const state = useSelector((state: RootState) => state.documents.byId.error);
  return documentIds.some((documentId) => state[documentId.value]);
}
export const getDocumentsQueryByInterface = (interfaceName: string) =>
  useSelector(
    (state: RootState) =>
      state.documents.interfaceQueries.entries[interfaceName]
  );
